import React, { useState, useEffect, useContext } from "react";
import ReusableModal from "./ReusableModal";
import config from "../../../../config/Global.json";
import "./Settings.css";
import FeatureSelectionModal from '../ManageSettings/FeatureSelectionModal'
import AuthContext from "../../../../utils/secure-route/AuthContext";
import useApi from "../../../../utils/api-manager/Helper/useApi";
import { toast, ToastContainer } from "react-toastify";
import {
  Building2,
  Crown,
  ShieldCheck,
  Store,
  UserCheck,
  UserPlus,
  Users,
} from "lucide-react";
import CustomSwitch from "./CustomSwitch";
import ManageUserTable from "./ManageUserTable";
import useSwal from "../../../common/ReusableComponent/useSwal";
import { capitalizeFirstLetter } from "../../../../utils/helper/common";
import { initialFormData } from "../../../common/FormHandler";
import { useToastHandler } from "../../../common/ToastHandler";
import { validateField } from "../../../../utils/api-manager/Helper/helper";
import useFormattingUtils from "../../../../context/useFormattingUtils";
import { useStore } from '../../../../context/StoreContext';
import Permissions from '../../../../config/permissions';
import PermissionGroupsSettings from './PermissionGroupsSettings';
import {
  RegularButton,
} from "../../../common/ReusableComponent/CustomButton";
import {
  SelectInput,
  ReusableInput,
} from "../../../common/ReusableComponent/ReusableInput";

const normalizePostalCode = (value = "") =>
  value.replace(/[^a-zA-Z0-9 -]/g, "").toUpperCase().slice(0, 10);

function ManageUsers() {
  const { user, permissions } = useContext(AuthContext);
  const storeId = user.store;
  const [activeSection, setActiveSection] = useState("users"); // "users" | "permission_groups"
  const [activeModal, setActiveModal] = useState(false);
  const [activeStoreModal, setActiveStoreModal] = useState(false);
  const [assignOwnerModal, setAssignOwnerModal] = useState(false);
  const [featureModal, setFeatureModal] = useState(false);
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [role, setRole] = useState("");
  const [status, setStatus] = useState(true);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [ownerEmail, setOwnerEmail] = useState("");
  const [tempUserData, setTempUserData] = useState(null);
  const { Get, _Post, Post, Patch, Delete, _Get, refreshCache, GetPaginatedData, PostWithId } = useApi();
  const [loading, setLoading] = useState(false);
  const [pincodeErrorMsg, setPincodeErrorMsg] = useState("");
  const [pincodeErrorFlag, setPincodeErrorFlag] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const { handleToast } = useToastHandler();
  const { formatDate } = useFormattingUtils();
  const { refreshStores } = useStore();
  const { confirmDelete, confirm } = useSwal();
  const [emailError, setEmailError] = useState("");
  const emailRegex = /^[\w\.-]+@[a-zA-Z\d\.-]+\.[a-zA-Z]{2,}$/;
  const [ownerEmailError, setOwnerEmailError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [refreshKey, setRefreshKey] = useState(0);
  const fetchUsersAndRoles = async ({ page = 1, searchTerm = "" } = {}) => {
    setLoading(true);
    try {
      const params = {
        page,
      };
      if (searchTerm.trim()) {
        params.search = searchTerm;
      }
      const response = await GetPaginatedData("user", params);
      const results = response?.data || [];
      const count = response?.totalCount || 0;
      const totalPages = Math.ceil(count / 10);
      setUsers(results);
      setTotalPages(totalPages);
      setCurrentPage(page);
      if (roles.length === 0) {
        try {
          const rolesResponse = await _Get("msRole");
          if (rolesResponse.error) throw new Error(rolesResponse.error);
          const roles = Array.isArray(rolesResponse.results)
            ? rolesResponse.results
            : [];
          setRoles(roles.map((role) => ({ id: role.id, name: role.role_name })));
        } catch (roleError) {
          console.error("Error fetching roles:", roleError);
          handleToast({
            type: "ERROR",
            message: "Error fetching roles.",
          });
          setRoles([]);
        }
      }
      return {
        data: results,
        totalPages,
      };
    } catch (error) {
      console.error("Error fetching data:", error);
      handleToast({
        type: "ERROR",
        message: "Error fetching users.",
      });
      setUsers([]);
      setTotalPages(1);
      setCurrentPage(1);
      return {
        data: [],
        totalPages: 1,
      };
    } finally {
      setLoading(false);
    }
  };
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };
  const handleShowUserModal = (user = null) => {
    if (user) {
      setCurrentUserId(user.id);
      setFullName(user.first_name);
      setEmail(user.email);
      setPhoneNumber(user.contact_no);
      setRole(user.ms_role);
      setStatus(user.is_active);
      const userPermissions = user.permissions || [];
      const selectedFeatures = [];
      Object.values(Permissions).forEach(permission => {
        if (userPermissions.includes(permission.code)) {
          selectedFeatures.push(permission.label);
        }
      });
      setTempUserData({
        fullName: user.first_name,
        email: user.email,
        phoneNumber: user.contact_no,
        role: user.ms_role,
        status: user.is_active,
        roleName: user.ms_role_name,
        currentUserId: user.id,
        previouslySelectedFeatures: selectedFeatures,
        previouslySelectedPermissions: userPermissions,
        apiPermissions: userPermissions,
        isEditing: true
      });
    } else {
      setCurrentUserId(null);
      setFullName("");
      setEmail("");
      setPhoneNumber("");
      setRole("");
      setStatus(true);
      setTempUserData(null);
    }
    setActiveModal(true);
  };
  const handleFullNameChange = (e) => {
    const value = e.target.value;
    setFullName(capitalizeFirstLetter(value));
  };
  const handleStoreName = (e) => {
    const value = e.target.value;
    setFormData((prevData) => ({
      ...prevData,
      store_name: capitalizeFirstLetter(value),
    }));
  };
  const handleStoreAddress = (e) => {
    const value = e.target.value;
    setFormData((prevData) => ({
      ...prevData,
      address_line1: capitalizeFirstLetter(value),
    }));
  };
  const handleStoreLocationField = (field) => (e) => {
    const value = e.target.value;
    setFormData((prevData) => ({
      ...prevData,
      [field]: capitalizeFirstLetter(value),
    }));
  };
  const handleCloseUserModal = () => setActiveModal(false);
  const handleCloseAssignOwnerModal = () => {
    setAssignOwnerModal(false);
    setOwnerEmail("");
  };
  const handleCloseStoreModal = () => {
    setActiveStoreModal(false);
    setFormData(initialFormData);
    setPincodeErrorFlag(false);
    setPincodeErrorMsg("");
  };
  const refreshData = () => {
    setRefreshKey(prev => prev + 1);
  }
  const handleCloseFeatureModal = () => {
    setFeatureModal(false);
    setTempUserData(null);
  };
  const clearUserForm = () => {
    setFullName("");
    setEmail("");
    setPhoneNumber("");
    setRole("");
    setStatus(true);
  };
  const handleDelete = async (id) => {
    const result = await confirmDelete()
    if (!result.isConfirmed) return;
    try {
      await Delete("user", id);
      setUsers((prev) => prev.filter((user) => user.id !== id));
      toast.success("User deleted successfully!");
    } catch (error) {
      console.error("Error deleting user:", error);
      const message =
        error?.response?.data?.detail || "Error deleting user!";
      toast.error(message);
    }
  };
  const handleToggleEmployeeAccess = async (employee) => {
    const enable = employee.is_active === false;
    const result = await confirm({
      title: `${enable ? "Enable" : "Disable"} employee access?`,
      text: enable
        ? `${employee.first_name} will be able to sign in to this store again.`
        : `${employee.first_name} will immediately lose access to this store.`,
      confirmButtonText: enable ? "Enable access" : "Disable access",
    });
    if (!result.isConfirmed) return;
    try {
      const response = await Patch("user", `${employee.id}/store-access`, { enabled: enable });
      toast.success(response.data?.detail || "Employee access updated.");
      await fetchUsersAndRoles({ page: currentPage });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to update employee access.");
    }
  };
  const handleResetEmployeePassword = async (employee) => {
    const result = await confirm({
      title: "Send password reset link?",
      text: `A secure one-time reset link will be emailed to ${employee.email}.`,
      confirmButtonText: "Send reset link",
    });
    if (!result.isConfirmed) return;
    try {
      const response = await PostWithId("user", `${employee.id}/send-password-reset/`, {});
      toast.success(response.data?.detail || "Password reset link sent.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to send the password reset link.");
    }
  };
  const handleNextUser = () => {
    const emailError = validateField(email, "email", "Email");
    const phoneError = validateField(phoneNumber, "text", "Phone Number");
    const fullNameError = validateField(fullName, "text", "Full Name");
    const roleError = validateField(role, "select", "Role");
    if (emailError || phoneError || fullNameError || roleError) {
      if (emailError) handleToast({ type: "ERROR", message: emailError });
      if (phoneError) handleToast({ type: "ERROR", message: phoneError });
      if (fullNameError) handleToast({ type: "ERROR", message: fullNameError });
      if (roleError) handleToast({ type: "ERROR", message: roleError });
      return;
    }
    const roleName = roles.find((r) => String(r.id) === String(role))?.name || "";
    let previousFeatures = [];
    let previousPermissions = [];
    if (currentUserId) {
      const currentUser = users.find(u => u.id === currentUserId);
      previousFeatures = currentUser?.selected_features || [];
      previousPermissions = currentUser?.permissions || [];
    }
    setTempUserData({
      fullName,
      email,
      phoneNumber,
      role,
      status,
      roleName,
      currentUserId,
      previouslySelectedFeatures: previousFeatures,
      previouslySelectedPermissions: previousPermissions,
      isEditing: !!currentUserId
    });
    setActiveModal(false);
    setFeatureModal(true);
  };
  const handleSaveUserWithFeatures = async (userFormData, selectedFeatures, selectedPermissionCodes) => {
    const payload = {
      first_name: userFormData.fullName,
      contact_no: userFormData.phoneNumber,
      ms_role: userFormData.role,
      store: storeId,
      ...(!userFormData.currentUserId ? { is_active: userFormData.status } : {}),
      groups: [],
      ms_role_name: userFormData.roleName,
      selected_features: selectedFeatures,
      permissions: selectedPermissionCodes || [],
    };

    if (!userFormData.currentUserId) {
      payload.email = userFormData.email;
    }
    try {
      let response;
      if (userFormData.currentUserId) {
        response = await Patch('user', userFormData.currentUserId, payload);
        if (response.status === 200) {
          toast.success('User updated successfully!');
        } else {
          console.log('User update failed:', response.data);
          toast.error(response.data?.message || response.data || 'Failed to update user.');
          return;
        }
      } else {
        response = await Post('user', payload);
        if (response.status === 201) {
          toast.success('User created successfully!');
        } else {
          console.log('User creation failed:', response.data);
          toast.error(response.data?.message || response.data || 'Failed to create user.');
          return;
        }
      }
      setTimeout(async () => {
        const updatedUsers = await _Get('user');
        setUsers(updatedUsers.results);
        handleCloseFeatureModal();
        clearUserForm();
      }, 1200); 
    } catch (error) {
      console.error('Error saving user:', error);
      const errorData = error.response?.data;
      if (errorData) {
        const messages = Object.values(errorData).flat().join('\n');
        console.log('Error toast:', messages);
        toast.error(messages || 'Error saving user!');
      } else {
        console.log('Error toast:', error.message);
        toast.error(error.message || 'Error saving user!');
      }
    }
  };
  const handleUserSaveError = (error) => {
    if (error.response && error.response.data) {
      if (error.response.data.ms_role === "You cannot change the role of a store owner.") {
        handleToast({
          type: "ERROR",
          message: "Store owner roles cannot be changed.",
        });
      } else if (error.response.data.is_active === "Store owners cannot be deactivated.") {
        handleToast({
          type: "ERROR",
          message: "Store owners cannot be deactivated.",
        });
      } else {
        handleToast({
          type: "ERROR",
          message: "Error saving user!",
        });
      }
    } else {
      handleToast({
        type: "ERROR",
        message: "Error saving user!",
      });
    }
  };
  const handleAssignOwnerSubmit = (e) => {
    e.preventDefault();
    Post("assignAsOwner", { email: ownerEmail })
      .then((response) => {
        _Post("user");
        refreshCache("user", "get");
        handleToast({
          type: "SUCCESS",
          message: "User assigned as owner successfully!",
        });
        setOwnerEmail("");
        fetchUsersAndRoles();
        handleCloseAssignOwnerModal();
      })
      .catch((error) => {
        console.error("Error assigning owner:", error);
        if (
          error.response &&
          error.response.data &&
          error.response.data.error === "User with this email does not exist."
        ) {
          handleToast({
            type: "ERROR",
            message: "The email you provided does not exist.",
          });
        } else {
          handleToast({
            type: "ERROR",
            message: "Error assigning owner!",
          });
        }
      });
  };
  const fetchLocationDetails = async (pincode) => {
    const apiUrl = `${config.api.host}${config.api.getPincodeData}`;
    try {
      const response = await Post(apiUrl, { pincode });
      const { city, state, country } = response.data;
      setFormData((prevData) => ({
        ...prevData,
        city: city || "",
        state: state || "",
        country: country || "",
      }));
      setPincodeErrorMsg("");
      setPincodeErrorFlag(false);
    } catch (error) {
      setPincodeErrorMsg("");
      setPincodeErrorFlag(false);
    }
  };
  const handlePincodeChange = (e) => {
    const { value } = e.target;
    const postalCode = normalizePostalCode(value);
    setFormData((prevData) => ({
      ...prevData,
      pincode: postalCode,
      ...(postalCode.trim() === "" && { city: "", state: "", country: "" }),
    }));

    setPincodeErrorMsg("");
    setPincodeErrorFlag(false);

    if (postalCode.trim().length >= 3) {
      fetchLocationDetails(postalCode);
    }
  };
  const handlePincodeBlur = () => {
    const { pincode } = formData;
    const postalCode = normalizePostalCode(pincode);

    if (!postalCode.trim()) {
      setPincodeErrorMsg("Postal code is required.");
      setPincodeErrorFlag(true);
      return;
    }

    setFormData((prevData) => ({ ...prevData, pincode: postalCode }));
    setPincodeErrorMsg("");
    setPincodeErrorFlag(false);
    fetchLocationDetails(postalCode);
  };
  const validateForm = () => {
    const { email, store_name, address_line1, pincode, city, state, country } =
      formData;
    if (
      !email ||
      !store_name ||
      !address_line1 ||
      !pincode ||
      !city ||
      !state ||
      !country
    ) {
      toast.error("All fields are required!");
      return false;
    }
    if (!normalizePostalCode(pincode).trim()) {
      toast.error("Postal code is required.");
      return false;
    }
    return true;
  };
  const handleSaveStore = async () => {
    if (!validateForm()) return;
    setLoading(true);
    try {
      await Post("additionalStore", formData);
      toast.success("Store added successfully!");
      refreshStores();
      setFormData({ initialFormData });
      setActiveStoreModal(false);
    } catch (error) {
      console.error("Error adding store:", error);
      toast.error("Failed to add store. Please try again.");
    } finally {
      setLoading(false);
    }
  };
  const handleShowStoreModal = async (store = null) => {
  try {
    if (store) {
      setFormData({ ...initialFormData });
      setActiveStoreModal(true);
      return;
    }
    const { results = [] } = await Get("user");
    if (!Array.isArray(results) || results.length === 0) {
      toast.error("No users found.");
      return;
    }
    const ownerUser = results.find(
      ({ ms_role_name }) =>
        ms_role_name &&
        ms_role_name.trim().toLowerCase() === "store owner"
    );
    if (!ownerUser?.email) {
      toast.error("No store owner found. Please assign an owner first.");
      return;
    }
    setFormData({
      ...initialFormData,
      email: ownerUser.email,
    });
    setActiveStoreModal(true);
  } catch (error) {
    console.error("Error fetching user data:", error);
    toast.error("Failed to load store owner information.");
  }
};
  const handleClearStore = () => {
    setFormData(initialFormData);
    setPincodeErrorFlag(false);
    setPincodeErrorMsg("");
  };
  const formattedManageUser = users.map(item => ({
    ...item,
    ms_role_name: item.membership_role_name || item.ms_role_name,
    is_active: item.store_access_enabled ?? item.is_active,
    date: formatDate(item.joining_date)
  }));
  const columns = [
    { header: "Name", field: "first_name" },
    { header: "Email Address", field: "email" },
    { header: "Phone Number", field: "contact_no" },
    { header: "Role", field: "ms_role_name" },
    { header: "Join Date", field: "date" },
    { header: "Status", field: "is_active" },
  ];
  const handleEmailBlur = () => {
    if (!email) {
      setEmailError("Email is required");
    } else if (!emailRegex.test(email)) {
      setEmailError("Please enter a valid email address");
    } else {
      setEmailError("");
    }
  };
  const handleEmailFocus = () => {
    setEmailError("");
  };
  const handleOwnerEmailBlur = () => {
    if (!ownerEmail) {
      setOwnerEmailError("Email is required");
    } else if (!emailRegex.test(ownerEmail)) {
      setOwnerEmailError("Please enter a valid email address");
    } else {
      setOwnerEmailError("");
    }
  };
  const handleOwnerEmailFocus = () => {
    setOwnerEmailError("");
  };
  const [userRole, setUserRole] = useState("");
  const handleUserProfile = () => {
  _Get("checkUserType")
    .then((response) => {
      setUserRole(response?.role_name || "");
    })
    .catch((error) => {
      console.error("Error fetching user role:", error);
    });
};
useEffect(() => {
  handleUserProfile();
}, []);
const normalizedUserRole = userRole.trim().toLowerCase();
const isEmployee = normalizedUserRole === "store employee";
const isStoreOwner = normalizedUserRole === "store owner";
const activeUsers = users.filter((item) => item.is_active !== false).length;
const disabledUsers = users.filter((item) => item.is_active === false).length;
const roleCount = new Set(users.map((item) => item.ms_role_name).filter(Boolean)).size;
  return (
    <>
      <ToastContainer
      />
      <div className="responsive-box manage-users-workspace">
        <div className="settings-hero user-page-hero">
          <div>
            <span className="settings-eyebrow">
              <Users size={16} />
              Team access
            </span>
            <h1>Manage Users</h1>
            <p>
              Create users, assign roles and configure permissions for people
              working across this store.
            </p>
          </div>
          {!isEmployee && activeSection === "users" && (
          <div className="user-hero-actions">
            <button
              type="button"
              className="settings-secondary-action user-action-button"
              onClick={() => setAssignOwnerModal(true)}
            >
              <Crown size={18} />
              Assign Owner
            </button>
            <button
              type="button"
              className="settings-secondary-action user-action-button"
              onClick={() => handleShowStoreModal()}
            >
              <Store size={18} />
              Add Store
            </button>
            <button
              type="button"
              className="settings-primary-action user-action-button"
              onClick={() => handleShowUserModal()}
            >
              <UserPlus size={18} />
              Add User
            </button>
          </div>
          )}
        </div>

        {!isEmployee && (
          <div style={{ display: "flex", gap: "10px", margin: "18px 0 22px", borderBottom: "1px solid #e5eaf3" }}>
            {[
              { id: "users", label: "Users", icon: Users },
              { id: "permission_groups", label: "Permission Groups", icon: ShieldCheck },
            ].map((sectionTab) => {
              const SectionIcon = sectionTab.icon;
              return (
                <button
                  key={sectionTab.id}
                  type="button"
                  onClick={() => setActiveSection(sectionTab.id)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    padding: "10px 18px",
                    border: "none",
                    background: "none",
                    cursor: "pointer",
                    fontSize: "14px",
                    fontWeight: 800,
                    color: activeSection === sectionTab.id ? "#2e7d32" : "#64748b",
                    borderBottom: activeSection === sectionTab.id ? "3px solid #2e7d32" : "3px solid transparent",
                  }}
                >
                  <SectionIcon size={16} />
                  {sectionTab.label}
                </button>
              );
            })}
          </div>
        )}

        {activeSection === "permission_groups" && !isEmployee && (
          <PermissionGroupsSettings />
        )}

        {activeSection === "users" && (
        <>
        <div className="user-summary-grid">
          <div className="user-summary-card">
            <span className="user-summary-icon blue"><Users size={22} /></span>
            <div>
              <small>Total Users</small>
              <strong>{users.length}</strong>
            </div>
          </div>
          <div className="user-summary-card">
            <span className="user-summary-icon green"><UserCheck size={22} /></span>
            <div>
              <small>Active</small>
              <strong>{activeUsers}</strong>
            </div>
          </div>
          <div className="user-summary-card">
            <span className="user-summary-icon amber"><ShieldCheck size={22} /></span>
            <div>
              <small>Roles In Use</small>
              <strong>{roleCount}</strong>
            </div>
          </div>
          <div className="user-summary-card">
            <span className="user-summary-icon purple"><Building2 size={22} /></span>
            <div>
              <small>Disabled</small>
              <strong>{disabledUsers}</strong>
            </div>
          </div>
        </div>

        <div className="user-table-panel">
          <ManageUserTable
            data={formattedManageUser}
            columns={columns}
            showAction={!isEmployee}
            showFooter={true}
            onEdit={(handleEdit) => handleShowUserModal(handleEdit)}
            onDelete={null}
            onToggleAccess={isStoreOwner ? handleToggleEmployeeAccess : null}
            onResetPassword={isStoreOwner ? handleResetEmployeePassword : null}
            loading={loading}
            currentPage={currentPage}
            onPageChange={handlePageChange}
            totalPages={totalPages}
            fetchData={fetchUsersAndRoles}
            refreshKey={refreshKey}
            searchData={""}
          />
        </div>
        </>
        )}
      </div>
      {activeModal && (
        <ReusableModal
          show={true}
          handleClose={handleCloseUserModal}
          title={currentUserId ? "Edit User" : "Add New User"}
          width="700px"
        >
          <form>
            <div className="user-modal-form">
              <div className="user-modal-grid">
                <ReusableInput
                  type="text"
                  value={fullName}
                  label="Full Name"
                  placeholder=""
                  onChange={handleFullNameChange}
                  width={"300px"}
                />
                <ReusableInput
                  type="email"
                  value={email}
                  label="Email"
                  placeholder=""
                  onChange={(e) => setEmail(e.target.value)}
                  onBlur={handleEmailBlur}
                  onFocus={handleEmailFocus}
                  width={"300px"}
                  error={emailError}
                />
                <ReusableInput
                  type="tel"
                  value={phoneNumber}
                  label="Phone Number"
                  placeholder=""
                  onChange={(e) => {
                    const input = e.target.value.replace(/[^0-9]/g, "");
                    if (input.length <= 10) {
                      setPhoneNumber(input);
                    }
                  }}
                  width={"300px"}
                />
                <SelectInput
                  value={role}
                  onChange={e => setRole(e.target.value)}
                  options={roles}
                  placeholder="Select Role"
                />
              </div>
              {!currentUserId && <div className="user-status-row">
                <div>
                  <strong>User Status</strong>
                  <span>Disabled users cannot access the store workspace.</span>
                </div>
                <CustomSwitch
                  label="Status"
                  setting={{ id: 'status', name: '' }}
                  isActive={status}
                  handleSwitchChange={() => setStatus(!status)}
                />
              </div>}
              <div className="user-modal-actions">
                {!currentUserId && (
                  <RegularButton
                    text="Clear"
                    onClick={clearUserForm}
                    backgroundColor="#ffffff"
                    color="282E26"
                  />
                )}
                <RegularButton
                  text={currentUserId ? "Update" : "Next"}
                  onClick={currentUserId ? handleNextUser : handleNextUser}
                  backgroundColor="#282E26"
                />
              </div>
            </div>
          </form>
        </ReusableModal>
      )}
      { }
      <FeatureSelectionModal
        show={featureModal}
        onClose={handleCloseFeatureModal}
        onSave={handleSaveUserWithFeatures}
        userFormData={tempUserData}
      />
      {activeStoreModal && (
        <ReusableModal
          show={true}
          handleClose={handleCloseStoreModal}
          title={"Add New Store"}
          width="700px"
        >
          <form>
            <div className="user-modal-form">
              <div className="user-modal-grid">
                <ReusableInput
                  type="text"
                  value={formData.store_name}
                  label="Store Name"
                  placeholder=""
                  onChange={handleStoreName}
                  width={"300px"}
                />
                <ReusableInput
                  type="text"
                  value={formData.address_line1}
                  label="Store Address"
                  placeholder=""
                  onChange={handleStoreAddress}
                  width={"300px"}
                />
                <ReusableInput
                  type="text"
                  value={formData.pincode}
                  label="Pincode"
                  placeholder="Postal / pin code"
                  onChange={handlePincodeChange}
                  onBlur={handlePincodeBlur}
                  maxLength={10}
                  width={"300px"}
                  error={pincodeErrorFlag ? pincodeErrorMsg : ""}
                />
                <ReusableInput
                  type="text"
                  value={formData.state}
                  label="State"
                  placeholder="Enter state"
                  onChange={handleStoreLocationField("state")}
                  width={"300px"}
                  required
                />
                <ReusableInput
                  type="text"
                  value={formData.city}
                  label="City"
                  placeholder="Enter city"
                  onChange={handleStoreLocationField("city")}
                  width={"300px"}
                  required
                />
                <ReusableInput
                  type="text"
                  value={formData.country}
                  label="Country"
                  placeholder="Enter country"
                  onChange={handleStoreLocationField("country")}
                  width={"300px"}
                  required
                />
              </div>
              <div className="user-modal-actions">
                <RegularButton
                  text="Clear"
                  onClick={handleClearStore}
                  backgroundColor="#ffffff"
                  color="282E26"
                />
                <RegularButton
                  text={loading ? "Submitting..." : "Save"}
                  onClick={handleSaveStore}
                  backgroundColor="#282e26"
                />
              </div>
            </div>
          </form>
        </ReusableModal>
      )}
      {assignOwnerModal && (
        <ReusableModal
          show={true}
          handleClose={handleCloseAssignOwnerModal}
          title="Assign Admin as Owner"
          width="600px"
        >
          <form>
            <div className="user-modal-form user-owner-form">
              <ReusableInput
                type="email"
                value={ownerEmail}
                onChange={(e) => setOwnerEmail(e.target.value)}
                label="Enter User Email"
                placeholder=""
                required
                onBlur={handleOwnerEmailBlur}
                onFocus={handleOwnerEmailFocus}
                error={ownerEmailError}
              />
              <div className="user-modal-actions">
                <RegularButton
                  text="Assign"
                  onClick={handleAssignOwnerSubmit}
                  backgroundColor="#282e26"
                />
              </div>
            </div>
          </form>
        </ReusableModal>
      )}
    </>
  );
}
export default ManageUsers;
